import { Routes, Route, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Home } from '@/pages/Home';
import { About } from '@/pages/About';
import { Contact } from '@/pages/Contact';
import { Gallery } from '@/pages/Gallery';
import { ServicesOverview } from '@/pages/services/ServicesOverview';
import { Gardening } from '@/pages/services/Gardening';
import { Cleaning } from '@/pages/services/Cleaning';
import { Taxidermy } from '@/pages/services/Taxidermy';
import { Privacy } from '@/pages/legal/Privacy';
import { Terms } from '@/pages/legal/Terms';
import { Disclaimer } from '@/pages/legal/Disclaimer';
import { Shipping } from '@/pages/legal/Shipping';
import { Accessibility } from '@/pages/legal/Accessibility';
import { AdminLogin } from '@/pages/admin/Login';
import { AdminDashboard } from '@/pages/admin/Dashboard';
import { LocationServicePage, seoPages } from '@/pages/seo';

/**
 * Scroll restoration and page transition component
 */
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);

  return null;
}

/**
 * Page transition wrapper
 */
function PageTransition({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [transitionStage, setTransitionStage] = useState('page-transition');

  useEffect(() => {
    if (location !== displayLocation) {
      setTransitionStage('page-transition');
      setDisplayLocation(location);
    }
  }, [location, displayLocation]);

  return (
    <div key={displayLocation.pathname} className={transitionStage}>
      {children}
    </div>
  );
}

/**
 * Main App component with routing and page transitions
 */
function App() {
  return (
    <>
      <ScrollToTop />
      <Layout>
        <PageTransition>
          <Routes>
            {/* Main Pages */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/gallery" element={<Gallery />} />

            {/* Services Pages */}
            <Route path="/services" element={<ServicesOverview />} />
            <Route path="/services/gardening" element={<Gardening />} />
            <Route path="/services/cleaning" element={<Cleaning />} />
            <Route path="/services/taxidermy" element={<Taxidermy />} />

            {/* Legal Pages */}
            <Route path="/legal/privacy" element={<Privacy />} />
            <Route path="/legal/terms" element={<Terms />} />
            <Route path="/legal/disclaimer" element={<Disclaimer />} />
            <Route path="/legal/shipping" element={<Shipping />} />
            <Route path="/legal/accessibility" element={<Accessibility />} />

            {/* Admin Pages */}
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />

            {/* SEO Landing Pages */}
            {seoPages.map((config) => (
              <Route
                key={config.slug}
                path={`/${config.slug}`}
                element={<LocationServicePage config={config} />}
              />
            ))}

            {/* 404 - Redirect to Home */}
            <Route path="*" element={<Home />} />
          </Routes>
        </PageTransition>
      </Layout>
    </>
  );
}

export default App;
